<template>
  <sheet-modal
    :visible="show"
    :title="title"
    :description="description"
    :cancel-text="resolvedCancelText"
    :confirm-text="resolvedConfirmText"
    :confirm-disabled="confirmDisabled"
    :close-on-overlay="closeOnOverlay"
    :show-close="showClose"
    :placement="placement"
    @close="handleDismiss"
    @cancel="handleDismiss"
    @confirm="handleConfirm"
  >
    <view v-if="avatar" class="modal-avatar">
      <image :src="avatar" class="avatar-img" mode="aspectFill" />
    </view>
    <slot />
  </sheet-modal>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SheetModal from '@/components/sheet-modal/index.vue';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    show: boolean;
    title: string;
    /** 主题样式说明文案（对应 sheet-modal description） */
    description?: string;
    avatar?: string;
    cancelText?: string;
    confirmText?: string;
    confirmDisabled?: boolean;
    closeOnOverlay?: boolean;
    showClose?: boolean;
    /** bottom：底部抽屉；center：居中弹框 */
    placement?: 'bottom' | 'center';
    /** 异步确认时保持打开，由父级关闭 */
    keepOpenOnConfirm?: boolean;
  }>(),
  {
    description: '',
    avatar: '',
    cancelText: undefined,
    confirmText: undefined,
    confirmDisabled: false,
    closeOnOverlay: true,
    showClose: true,
    placement: 'bottom',
    keepOpenOnConfirm: false,
  }
);

const resolvedCancelText = computed(() => props.cancelText ?? t('common.cancel'));
const resolvedConfirmText = computed(() => props.confirmText ?? t('common.confirm'));

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'cancel'): void;
  (e: 'confirm'): void;
}>();

/** sheet 的 cancel 会同时抛 close，合并为一次，避免父级收到两次取消 */
let dismissLock = false;
const handleDismiss = () => {
  if (dismissLock) return;
  dismissLock = true;
  emit('update:show', false);
  emit('cancel');
  queueMicrotask(() => {
    dismissLock = false;
  });
};

const handleConfirm = () => {
  if (props.confirmDisabled) return;
  emit('confirm');
  if (!props.keepOpenOnConfirm) {
    emit('update:show', false);
  }
};
</script>

<style lang="scss" scoped>
.modal-avatar {
  display: flex;
  justify-content: center;
  margin-bottom: 8rpx;

  .avatar-img {
    width: 160rpx;
    height: 160rpx;
    border-radius: 50%;
    overflow: hidden;
    background-color: #3aa0ff;
  }
}
</style>
